
// Shared controller for a project's "labels" — the Projects-app feature the user asked to mirror. A label
// here is an ObservationTagProjectJoin: a free-text `Tag` of a given `TagType` scoped to a project, which is
// what observations in that project get tagged with. (Contrast with LabelManagerController, which manages
// ProjectTagType — the *category vocabulary* the TagType dropdown below is drawn from.)
//
// Mirrors the LabelManagerController shape: a plain class (NOT a pict provider) driven by a host provider,
// exporting the shared list template (OBSLABEL_TEMPLATES) + CSS (OBSLABEL_CSS). Template handlers call back
// into the host by name via Record.ProviderName. Its own `.otl-` namespace keeps it decoupled from the
// ProjectTagType manager (which My Headlight also renders).
//
// SERVER-PAGINATED: a project can have tens of thousands of labels (10k+ of a single type), so the list is
// filtered/searched/paged on the server (a single one-page render at a time), never loaded whole:
//   - count:  GET ObservationTagProjectJoins/Count/FilteredTo/<filter>
//   - page:   GET ObservationTagProjectJoins/FilteredTo/<filter>~FSF~Tag~ASC~0/<offset>/<pageSize>
//     filter = FBV~IDProject~EQ~<id> [ ~FBV~TagType~EQ~<type> ] [ ~FBV~Tag~LK~%<search>% ]
//   - create: POST ObservationTagProjectJoin { Tag, TagType, IDProject }   (server mints/links the
//             ObservationTag + derives IDCustomer from the session — never send IDCustomer)
//   - remove: DELETE ObservationTagProjectJoin/<id>
// TagType options come from the project's ProjectTagTypes, falling back to the customer defaults (IDProject=0)
// then Headlight's platform defaults (IDProject=0, IDCustomer=0) — the same effective set the Projects app offers.

const PAGE_SIZE = 50;

const libConfigSync = require('./ConfigSync.js');

class ProjectObservationLabelController
{
	/**
	 * @param {any} pPict
	 * @param {String} pProviderName - the host provider's identifier (the list template calls it back).
	 */
	constructor(pPict, pProviderName)
	{
		this.pict = pPict;
		this.providerName = pProviderName;

		this.scope = { IDProject: 0, IDCustomer: 0 };
		this.canManage = false;
		this._onChange = () => {};

		// The current PAGE of the project's labels (ObservationTagProjectJoin rows).
		this.labels = [];
		// The effective TagType vocabulary for the ADD-row select (project types, else customer defaults,
		// else platform defaults).
		this.tagTypes = [];
		// The distinct TagTypes actually PRESENT in this project's labels — the type-filter is the union of
		// these and the vocabulary, so you can filter by a type that exists in the data (e.g. imported
		// BidItem labels) even when it isn't in the customer's type set. Loaded once, refreshed after writes.
		this.presentTypes = [];
		this._presentTypesLoaded = false;
		this.loading = false;
		this.busy = false;
		// Add-row state (stashed on every keystroke/pick WITHOUT a re-render so the inputs keep focus).
		this.newTag = '';
		this.newTagType = '';
		// Bulk-add panel state — stashed on the controller (not left in the DOM) so an unrelated full re-render
		// (e.g. a label-TYPE change in the sibling panel) can't silently discard an unsubmitted paste.
		this.bulkTag = '';
		this.bulkTagType = '';
		// Filter / search / paging state. `searchDraft` is the live input buffer (stashed without a render so
		// typing keeps focus); `search` is the applied term (Enter / Search button). `filterType` scopes to one
		// TagType. `offset`/`total` drive Prev/Next over the server-side result.
		this.searchDraft = '';
		this.search = '';
		this.filterType = '';
		this.offset = 0;
		this.pageSize = PAGE_SIZE;
		this.total = 0;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	_toast(pMessage, pType)
	{
		if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'success' }); }
	}

	/**
	 * After a label change, (debounced) publish this project's config to the field apps (Fieldbook) — without
	 * it, a new/removed label shows here but never reaches Fieldbook. Debounced + permission-gated in ConfigSync.
	 */
	_requestConfigSync()
	{
		if (Number(this.scope.IDProject) > 0)
		{
			libConfigSync.requestSync(this.pict, 'Project', this.scope.IDProject);
		}
	}

	// --- REST (promisified) ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_create(pRecord)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.createEntity('ObservationTagProjectJoin', pRecord, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_delete(pID)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.deleteEntity('ObservationTagProjectJoin', pID, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/**
	 * Point the controller at a project scope + permission and register the host's re-render.
	 * @param {{IDProject:Number, IDCustomer:Number}} pScope
	 * @param {Boolean} pCanManage
	 * @param {Function} pOnChange
	 */
	setContext(pScope, pCanManage, pOnChange)
	{
		this.scope = pScope || { IDProject: 0, IDCustomer: 0 };
		this.canManage = !!pCanManage;
		this._onChange = (typeof pOnChange === 'function') ? pOnChange : () => {};
		this.labels = [];
		this.tagTypes = [];
		this.presentTypes = [];
		this._presentTypesLoaded = false;
		this.newTag = '';
		this.newTagType = '';
		this.bulkTag = '';
		this.bulkTagType = '';
		this.searchDraft = '';
		this.search = '';
		this.filterType = '';
		this.offset = 0;
		this.total = 0;
	}

	// --- Data ops ---

	/** The FoxHound filter for the current project + active type filter + search term (no sort stanza). */
	_baseFilter()
	{
		let tmpFilter = `FBV~IDProject~EQ~${this.scope.IDProject}`;
		if (this.filterType) { tmpFilter += `~FBV~TagType~EQ~${encodeURIComponent(this.filterType)}`; }
		if (this.search) { tmpFilter += `~FBV~Tag~LK~${encodeURIComponent(`%${this.search}%`)}`; }
		return tmpFilter;
	}

	async load()
	{
		this.loading = true;
		this._onChange();
		try
		{
			await this._loadTagTypes();
			// The type-filter's option set (distinct types present in the data) — loaded once, then refreshed
			// after a write; navigation (page/search/filter) reuses it.
			if (!this._presentTypesLoaded) { await this._loadPresentTypes(); }
			const tmpBaseFilter = this._baseFilter();
			const tmpCount = await this._getJSON(`ObservationTagProjectJoins/Count/FilteredTo/${tmpBaseFilter}`);
			this.total = Number(tmpCount && (tmpCount.Count != null) ? tmpCount.Count : tmpCount) || 0;
			// Clamp the offset if it now sits past the end (after a filter change or a delete emptied the tail).
			if (this.offset > 0 && this.offset >= this.total)
			{
				this.offset = Math.max(0, (Math.ceil(this.total / this.pageSize) - 1) * this.pageSize);
			}
			const tmpLabels = await this._getJSON(`ObservationTagProjectJoins/FilteredTo/${tmpBaseFilter}~FSF~Tag~ASC~0/${this.offset}/${this.pageSize}`);
			this.labels = Array.isArray(tmpLabels) ? tmpLabels.slice() : [];
		}
		catch (pError)
		{
			this.labels = [];
			this.total = 0;
			this._toast(`Could not load labels: ${pError.message || pError}`, 'error');
		}
		this.loading = false;
		this._onChange();
	}

	/**
	 * The effective TagType vocabulary, cascaded like the manifest sibling + the legacy Projects app:
	 * the project's own ProjectTagTypes, else the customer defaults (IDProject=0), else Headlight's
	 * platform defaults (IDProject=0, IDCustomer=0).
	 */
	async _loadTagTypes()
	{
		const fSort = (pRows) => (Array.isArray(pRows) ? pRows.slice() : []).sort((a, b) => (Number(a.Ordinal || 0) - Number(b.Ordinal || 0)) || String(a.TagType || '').localeCompare(String(b.TagType || '')));
		let tmpTypes = fSort(await this._getJSON(`ProjectTagTypes/FilteredTo/FBV~IDProject~EQ~${this.scope.IDProject}~FBV~IDCustomer~EQ~${this.scope.IDCustomer}/0/1000`));
		if (tmpTypes.length === 0)
		{
			tmpTypes = fSort(await this._getJSON(`ProjectTagTypes/FilteredTo/FBV~IDProject~EQ~0~FBV~IDCustomer~EQ~${this.scope.IDCustomer}/0/1000`));
		}
		if (tmpTypes.length === 0)
		{
			tmpTypes = fSort(await this._getJSON('ProjectTagTypes/FilteredTo/FBV~IDProject~EQ~0~FBV~IDCustomer~EQ~0/0/1000'));
		}
		this.tagTypes = tmpTypes;
		// Default the add-row type to the first available (or keep a still-valid prior pick).
		const tmpCodes = this.tagTypes.map((pType) => String(pType.TagType));
		if (!this.newTagType || tmpCodes.indexOf(String(this.newTagType)) === -1)
		{
			this.newTagType = (this.tagTypes[0] && this.tagTypes[0].TagType) || '';
		}
		if (!this.bulkTagType || tmpCodes.indexOf(String(this.bulkTagType)) === -1)
		{
			this.bulkTagType = (this.tagTypes[0] && this.tagTypes[0].TagType) || '';
		}
		// NOTE: the type FILTER is intentionally NOT reset to a vocabulary type here — labels can exist on a
		// type that was later turned off (removed from the vocabulary), and filtering by such a type must stay
		// possible. The filter option set is the union of the vocabulary and the distinct present types.
	}

	/** Load the distinct TagTypes actually present in this project's labels (for the type-filter options). */
	async _loadPresentTypes()
	{
		try
		{
			const tmpRows = await this._getJSON(`ObservationTagProjectJoins/Distinct/TagType/FilteredTo/FBV~IDProject~EQ~${this.scope.IDProject}`);
			this.presentTypes = (Array.isArray(tmpRows) ? tmpRows : [])
				.map((pRow) => pRow && pRow.TagType)
				.filter((pType) => (pType != null) && (String(pType).trim() !== ''));
		}
		catch (pError)
		{
			this.presentTypes = [];
		}
		this._presentTypesLoaded = true;
	}

	/** Stash the add-row Tag WITHOUT a re-render (keeps the input focused while typing). */
	setNewTag(pValue)
	{
		this.newTag = pValue;
	}

	/** Stash the picked TagType WITHOUT a re-render (the <select> already reflects the choice). */
	pickTagType(pValue)
	{
		this.newTagType = pValue;
	}

	/** Stash the bulk-add paste WITHOUT a re-render, so an unrelated full render re-emits it (no data loss). */
	setBulkTag(pValue)
	{
		this.bulkTag = pValue;
	}

	/** Stash the bulk-add TagType WITHOUT a re-render. */
	pickBulkType(pValue)
	{
		this.bulkTagType = pValue;
	}

	// --- Filter / search / paging ---

	/** Stash the search box WITHOUT a re-render (keeps focus while typing; applied on Enter / Search). */
	setSearch(pValue)
	{
		this.searchDraft = pValue;
	}

	/** Apply the typed search term (Enter or the Search button): reset to the first page and reload. */
	applySearch()
	{
		const tmpTerm = String(this.searchDraft || '').trim();
		if (tmpTerm === this.search) { return; }
		this.search = tmpTerm;
		this.offset = 0;
		this.load();
	}

	/** Clear the search term and reload. */
	clearSearch()
	{
		if (!this.search && !this.searchDraft) { return; }
		this.searchDraft = '';
		this.search = '';
		this.offset = 0;
		this.load();
	}

	/** Scope the list to one TagType (or '' for all): reset to the first page and reload. */
	setFilterType(pValue)
	{
		const tmpValue = String(pValue == null ? '' : pValue);
		if (tmpValue === this.filterType) { return; }
		this.filterType = tmpValue;
		this.offset = 0;
		this.load();
	}

	/** Move one page forward (+1) or back (-1) within the server-side result. */
	page(pDelta)
	{
		const tmpNext = this.offset + (Number(pDelta) * this.pageSize);
		if (tmpNext < 0 || tmpNext >= this.total) { return; }
		this.offset = tmpNext;
		this.load();
	}

	async add()
	{
		if (!this.canManage) { return; }
		const tmpTagType = String(this.newTagType || '').trim();
		if (!tmpTagType) { this._toast('Pick a label type first.', 'error'); return; }
		// Parity with the Projects app: a single field can add several labels at once, split on "|".
		const tmpTags = String(this.newTag || '').split('|').map((pTag) => pTag.trim()).filter((pTag) => pTag.length > 0);
		if (tmpTags.length === 0) { this._toast('Enter a label.', 'error'); return; }

		this.busy = true;
		this._onChange();
		let tmpAdded = 0;
		let tmpFailed = 0;
		for (let i = 0; i < tmpTags.length; i++)
		{
			const tmpTag = tmpTags[i];
			try
			{
				const tmpSaved = await this._create({ Tag: tmpTag, TagType: tmpTagType, IDProject: this.scope.IDProject });
				if (!tmpSaved || !tmpSaved.IDObservationTagProjectJoin)
				{
					// The server enforces uniqueness — a duplicate comes back as an Error body, not a throw.
					const tmpError = (tmpSaved && (tmpSaved.Error || tmpSaved.ErrorCode)) ? String(tmpSaved.Error || tmpSaved.ErrorCode) : '';
					if (/exist/i.test(tmpError)) { tmpFailed++; continue; }
					throw new Error(tmpError || 'the server returned an unexpected response.');
				}
				tmpAdded++;
			}
			catch (pError)
			{
				tmpFailed++;
				this._toast(`Could not add “${tmpTag}”: ${pError.message || pError}`, 'error');
			}
		}
		if (tmpAdded > 0)
		{
			this.newTag = '';
			const tmpNote = tmpFailed > 0 ? ` (${tmpFailed} already present or failed)` : '';
			this._toast(`Added ${tmpAdded} label${tmpAdded === 1 ? '' : 's'}${tmpNote}.`);
			this._requestConfigSync();
		}
		else if (tmpFailed > 0)
		{
			this._toast('That label is already on this project.', 'error');
		}
		this.busy = false;
		// Reload the current page + count (and refresh the present-types filter — a new type may now exist).
		this._presentTypesLoaded = false;
		await this.load();
	}

	/**
	 * Bulk-add many labels of ONE chosen type in a single action (the Projects-app "paste a list" feature).
	 * Accepts an array or a raw blob split on newlines / commas / pipes; de-dupes the paste, then creates
	 * each (server uniqueness rejects a duplicate as an Error body, not a throw — tallied, not fatal).
	 * @param {String} pTagType
	 * @param {String|String[]} pTags
	 */
	async bulkAdd(pTagType, pTags)
	{
		if (!this.canManage) { return; }
		const tmpTagType = String(pTagType || '').trim();
		if (!tmpTagType) { this._toast('Pick a label type first.', 'error'); return; }
		const tmpRaw = Array.isArray(pTags) ? pTags : String(pTags == null ? '' : pTags).split(/[\n,|]/);
		const tmpSeen = new Set();
		const tmpTags = tmpRaw
			.map((pTag) => String(pTag).trim())
			.filter((pTag) => pTag.length > 0)
			.filter((pTag) => { const tmpKey = pTag.toLowerCase(); if (tmpSeen.has(tmpKey)) { return false; } tmpSeen.add(tmpKey); return true; });
		if (tmpTags.length === 0) { this._toast('Enter one or more labels to add.', 'error'); return; }

		this.busy = true;
		this._onChange();
		let tmpAdded = 0;
		let tmpFailed = 0;
		for (let i = 0; i < tmpTags.length; i++)
		{
			const tmpTag = tmpTags[i];
			try
			{
				const tmpSaved = await this._create({ Tag: tmpTag, TagType: tmpTagType, IDProject: this.scope.IDProject });
				if (!tmpSaved || !tmpSaved.IDObservationTagProjectJoin)
				{
					const tmpError = (tmpSaved && (tmpSaved.Error || tmpSaved.ErrorCode)) ? String(tmpSaved.Error || tmpSaved.ErrorCode) : '';
					if (/exist/i.test(tmpError)) { tmpFailed++; continue; }
					throw new Error(tmpError || 'the server returned an unexpected response.');
				}
				tmpAdded++;
			}
			catch (pError)
			{
				tmpFailed++;
			}
		}
		if (tmpAdded > 0)
		{
			// Consumed — clear the stashed paste (kept on total failure so the user can fix and retry).
			this.bulkTag = '';
			const tmpNote = tmpFailed > 0 ? ` (${tmpFailed} already present or failed)` : '';
			this._toast(`Added ${tmpAdded} label${tmpAdded === 1 ? '' : 's'}${tmpNote}.`);
			this._requestConfigSync();
		}
		else
		{
			this._toast('No new labels were added — they may already be present on this project.', 'error');
		}
		this.busy = false;
		this._presentTypesLoaded = false;
		await this.load();
	}

	remove(pID)
	{
		if (!this.canManage) { return; }
		const tmpLabel = this.labels.find((pLabel) => pLabel.IDObservationTagProjectJoin === pID);
		if (!tmpLabel) { return; }
		const fApply = async () =>
		{
			try
			{
				await this._delete(pID);
				this._toast(`Removed the “${tmpLabel.Tag}” label.`);
				this._requestConfigSync();
			}
			catch (pError)
			{
				this._toast(`Could not remove the label: ${pError.message || pError}`, 'error');
			}
			// Reload the page + count (the row is gone; the tail may shift up; a type may now be empty).
			this._presentTypesLoaded = false;
			await this.load();
		};
		if (this.modal && (typeof this.modal.confirm === 'function'))
		{
			this.modal.confirm(`Remove the “${tmpLabel.Tag}” label from this project?`,
				{ title: 'Remove label', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { fApply(); } });
		}
		else { fApply(); }
	}

	// --- Display ---

	_escape(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	/** Build the render record the shared Headlight-ObsLabelManager template consumes. */
	buildState()
	{
		const tmpRows = this.labels.map((pLabel) =>
		{
			const tmpTag = (pLabel.Tag != null && String(pLabel.Tag).trim() !== '') ? pLabel.Tag : '(unnamed)';
			return {
				ProviderName: this.providerName,
				ID: pLabel.IDObservationTagProjectJoin,
				Tag: this._escape(tmpTag),
				TagType: this._escape(pLabel.TagType || '—'),
				RemoveSlot: (this.canManage && !this.busy) ? [ { ProviderName: this.providerName, ID: pLabel.IDObservationTagProjectJoin } ] : [],
			};
		});
		const tmpTypeOptions = this.tagTypes.map((pType) =>
			(
				{
					Value: this._escape(pType.TagType),
					Text: this._escape((pType.Name != null && String(pType.Name).trim() !== '') ? pType.Name : pType.TagType),
					SelectedAttr: (String(pType.TagType) === String(this.newTagType)) ? 'selected' : '',
				}));
		// Same options for the bulk-add type select, but its selection tracks bulkTagType (a separate control).
		const tmpBulkTypeOptions = this.tagTypes.map((pType) =>
			(
				{
					Value: this._escape(pType.TagType),
					Text: this._escape((pType.Name != null && String(pType.Name).trim() !== '') ? pType.Name : pType.TagType),
					SelectedAttr: (String(pType.TagType) === String(this.bulkTagType)) ? 'selected' : '',
				}));
		const tmpHasTypes = tmpTypeOptions.length > 0;
		const tmpFiltering = !!(this.search || this.filterType);
		// Type-filter options: the vocabulary types (display names, in order) + any types PRESENT in the data
		// but not in the vocabulary (labels left on a since-removed type), appended alpha and marked
		// "(inactive)" so it's clear they're no longer in the add-a-label vocabulary but can still be filtered.
		const tmpVocabByCode = {};
		this.tagTypes.forEach((pType) => { tmpVocabByCode[String(pType.TagType).toLowerCase()] = pType; });
		const tmpFilterCodes = this.tagTypes.map((pType) => String(pType.TagType));
		const tmpExtraPresent = (this.presentTypes || [])
			.map((pCode) => String(pCode))
			.filter((pCode) => !tmpVocabByCode[pCode.toLowerCase()] && (tmpFilterCodes.indexOf(pCode) === -1))
			.sort((pA, pB) => pA.localeCompare(pB));
		const tmpFilterOptions = [ { Value: '', Text: 'All types', SelectedAttr: (this.filterType === '') ? 'selected' : '' } ].concat(
			tmpFilterCodes.concat(tmpExtraPresent).map((pCode) =>
			{
				const tmpVocabRow = tmpVocabByCode[String(pCode).toLowerCase()];
				const tmpText = tmpVocabRow
					? ((tmpVocabRow.Name != null && String(tmpVocabRow.Name).trim() !== '') ? tmpVocabRow.Name : tmpVocabRow.TagType)
					: `${pCode} (inactive)`;
				return {
					Value: this._escape(pCode),
					Text: this._escape(tmpText),
					SelectedAttr: (String(pCode) === String(this.filterType)) ? 'selected' : '',
				};
			}));
		// Add-row shows only when the user can manage AND there is at least one type to pick.
		const tmpAddSlot = (this.canManage && !this.busy && tmpHasTypes)
			? [ { ProviderName: this.providerName, NewTag: this._escape(this.newTag), TagTypeOptions: tmpTypeOptions } ]
			: [];
		const tmpHasTable = (tmpRows.length > 0) || tmpAddSlot.length > 0;
		const tmpFrom = (this.total === 0) ? 0 : (this.offset + 1);
		const tmpTo = Math.min(this.offset + this.labels.length, this.total);
		return {
			ProviderName: this.providerName,
			LoadingSlot: this.loading ? [ {} ] : [],
			NoTypesSlot: (!this.loading && this.canManage && !tmpHasTypes) ? [ {} ] : [],
			// Filter bar: available whenever the project has types to filter/search by (independent of whether
			// the current page has rows), so you can always narrow a huge list or clear a too-tight filter.
			FilterSlot: (!this.loading && tmpHasTypes) ? [ {
				ProviderName: this.providerName,
				Search: this._escape(this.searchDraft),
				FilterOptions: tmpFilterOptions,
				ClearSlot: (this.search || this.searchDraft) ? [ { ProviderName: this.providerName } ] : [],
				CountLabel: this.total ? `${this.total.toLocaleString()} label${this.total === 1 ? '' : 's'}${tmpFiltering ? ' (filtered)' : ''}` : '',
			} ] : [],
			TableSlot: (!this.loading && tmpHasTable) ? [ {
				Labels: tmpRows,
				AddSlot: tmpAddSlot,
				BusySlot: this.busy ? [ {} ] : [],
			} ] : [],
			// Bulk-add panel (a collapsed "paste a list" affordance): available whenever the user can manage
			// and there is at least one type to add under. Reuses the add-row's type options.
			BulkSlot: (!this.loading && this.canManage && !this.busy && tmpHasTypes)
				? [ { ProviderName: this.providerName, TagTypeOptions: tmpBulkTypeOptions, BulkTag: this._escape(this.bulkTag) } ]
				: [],
			// Pager only when the result spans more than one page.
			PagerSlot: (!this.loading && this.total > this.pageSize) ? [ {
				ProviderName: this.providerName,
				RangeLabel: `${tmpFrom.toLocaleString()}–${tmpTo.toLocaleString()} of ${this.total.toLocaleString()}`,
				PrevDisabled: (this.offset <= 0) ? 'disabled' : '',
				NextDisabled: ((this.offset + this.pageSize) >= this.total) ? 'disabled' : '',
			} ] : [],
			// "No matches" only when a filter/search yields nothing (distinct from a project with no labels yet).
			NoMatchSlot: (!this.loading && tmpFiltering && this.total === 0) ? [ { ProviderName: this.providerName } ] : [],
			EmptySlot: (!this.loading && !tmpFiltering && this.total === 0 && !this.canManage) ? [ {} ] : [],
			ReadOnlySlot: (!this.canManage && !this.loading) ? [ {} ] : [],
		};
	}
}

module.exports = ProjectObservationLabelController;

module.exports.OBSLABEL_CSS = /*css*/`
	.otl-mgr { font-size: 0.92rem; }
	.otl-filter { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; margin: 0 0 0.7rem; }
	.otl-filter-search { display: flex; align-items: center; gap: 0.35rem; flex: 1 1 16rem; min-width: 12rem; }
	.otl-filter-search .input { flex: 1 1 auto; min-width: 0; }
	.otl-filter-type { flex: 0 0 auto; min-width: 9rem; }
	.otl-filter-count { flex: 0 0 auto; margin-left: auto; font-size: 0.8rem; color: var(--theme-color-text-muted, #6b7686); }
	.otl-table { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; overflow: hidden; }
	.otl-table th { text-align: left; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686);
		background: var(--theme-color-background-tertiary, #f4f6f9); padding: 0.45rem 0.8rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); }
	.otl-th-type { width: 26%; }
	.otl-th-act { width: 1%; }
	.otl-table td { padding: 0.45rem 0.8rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); vertical-align: middle; }
	.otl-table tbody tr:last-child td { border-bottom: none; }
	.otl-tag { font-weight: 650; color: var(--theme-color-text-primary, #1f2733); }
	.otl-type-badge { display: inline-block; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.02em; color: var(--theme-color-brand-primary, #5650e6);
		background: var(--theme-color-background-selected, #eef1f8); border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 999px; padding: 0.1rem 0.6rem; }
	.otl-input { max-width: 22rem; }
	.otl-act { text-align: right; white-space: nowrap; }
	.otl-add td { background: var(--theme-color-background-tertiary, #fafbfc); }
	.otl-pager { display: flex; align-items: center; gap: 0.6rem; margin-top: 0.7rem; }
	.otl-pager .otl-range { font-size: 0.82rem; color: var(--theme-color-text-muted, #6b7686); margin-right: auto; }
	.otl-bulk { margin-top: 0.9rem; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; background: var(--theme-color-background-tertiary, #fafbfc); }
	.otl-bulk > summary { cursor: pointer; font-weight: 650; font-size: 0.85rem; color: var(--theme-color-text-secondary, #45505f); padding: 0.55rem 0.85rem; list-style: none; display: flex; align-items: center; gap: 0.4rem; }
	.otl-bulk > summary::-webkit-details-marker { display: none; }
	.otl-bulk > summary::before { content: '▸'; font-size: 0.8rem; color: var(--theme-color-text-muted, #6b7686); transition: transform 0.15s ease; }
	.otl-bulk[open] > summary::before { transform: rotate(90deg); }
	.otl-bulk-body { padding: 0 0.85rem 0.85rem; display: flex; flex-direction: column; gap: 0.55rem; }
	.otl-bulk-row { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
	.otl-bulk-label { font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #6b7686); }
	.otl-bulk-type { max-width: 16rem; }
	.otl-bulk-text { width: 100%; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.82rem; }
	.otl-bulk-actions { display: flex; justify-content: flex-end; }
	.otl-empty { color: var(--theme-color-text-muted, #6b7686); padding: 0.6rem 0.2rem; }
	.otl-ro-note { font-size: 0.84rem; color: var(--theme-color-text-muted, #6b7686); font-style: italic; margin-top: 0.6rem; }
	.otl-notypes { display: flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; color: var(--theme-color-text-secondary, #45505f);
		background: var(--theme-color-background-tertiary, #f4f6f9); border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; padding: 0.6rem 0.85rem; margin: 0 0 0.85rem; line-height: 1.5; }
	.otl-notypes .icon { color: var(--theme-color-brand-primary, #5650e6); flex: 0 0 auto; }
	.otl-loading { color: var(--theme-color-text-muted, #6b7686); padding: 0.8rem 0.2rem; }
	.otl-spin { display: inline-block; width: 1rem; height: 1rem; border: 2px solid var(--theme-color-border-default, #dbdbdb); border-top-color: var(--theme-color-brand-primary, #5650e6); border-radius: 50%; animation: otl-spin 0.7s linear infinite; vertical-align: -0.15rem; margin-right: 0.4rem; }
	@keyframes otl-spin { to { transform: rotate(360deg); } }
`;

module.exports.OBSLABEL_TEMPLATES =
[
	{
		Hash: 'Headlight-ObsLabelManager',
		Template: /*html*/`
			<div class="otl-mgr">
				{~TS:Headlight-ObsLabelLoading:Record.LoadingSlot~}
				{~TS:Headlight-ObsLabelNoTypes:Record.NoTypesSlot~}
				{~TS:Headlight-ObsLabelFilter:Record.FilterSlot~}
				{~TS:Headlight-ObsLabelTable:Record.TableSlot~}
				{~TS:Headlight-ObsLabelNoMatch:Record.NoMatchSlot~}
				{~TS:Headlight-ObsLabelPager:Record.PagerSlot~}
				{~TS:Headlight-ObsLabelBulk:Record.BulkSlot~}
				{~TS:Headlight-ObsLabelEmpty:Record.EmptySlot~}
				{~TS:Headlight-ObsLabelReadOnly:Record.ReadOnlySlot~}
			</div>`
	},
	{
		Hash: 'Headlight-ObsLabelFilter',
		Template: /*html*/`
			<div class="otl-filter">
				<div class="otl-filter-search">
					<input class="input is-small" type="text" placeholder="Search labels…" value="{~D:Record.Search~}" oninput="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelSetSearch(this.value)" onkeydown="if (event.key === 'Enter') { event.preventDefault(); _Pict.providers['{~D:Record.ProviderName~}'].obsLabelApplySearch(); }" />
					<button class="button is-small" title="Search labels" onclick="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelApplySearch()"><span class="icon"><i class="fas fa-magnifying-glass"></i></span></button>
					{~TS:Headlight-ObsLabelClear:Record.ClearSlot~}
				</div>
				<select class="input is-small otl-filter-type" title="Filter by label type" onchange="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelSetFilterType(this.value)">{~TS:Headlight-ObsLabelFilterOption:Record.FilterOptions~}</select>
				<span class="otl-filter-count">{~D:Record.CountLabel~}</span>
			</div>`
	},
	{
		Hash: 'Headlight-ObsLabelFilterOption',
		Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Text~}</option>`
	},
	{
		Hash: 'Headlight-ObsLabelClear',
		Template: /*html*/`<button class="button is-small is-light" title="Clear search" onclick="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelClearSearch()"><span class="icon"><i class="fas fa-xmark"></i></span></button>`
	},
	{
		Hash: 'Headlight-ObsLabelTable',
		Template: /*html*/`
			<table class="otl-table">
				<thead><tr><th>Label</th><th class="otl-th-type">Type</th><th class="otl-th-act"></th></tr></thead>
				<tbody>
					{~TS:Headlight-ObsLabelAdd:Record.AddSlot~}
					{~TS:Headlight-ObsLabelRow:Record.Labels~}
				</tbody>
			</table>`
	},
	{
		Hash: 'Headlight-ObsLabelRow',
		Template: /*html*/`
			<tr class="otl-row">
				<td class="otl-tag">{~D:Record.Tag~}</td>
				<td><span class="otl-type-badge">{~D:Record.TagType~}</span></td>
				<td class="otl-act">{~TS:Headlight-ObsLabelRemove:Record.RemoveSlot~}</td>
			</tr>`
	},
	{
		Hash: 'Headlight-ObsLabelRemove',
		Template: /*html*/`<button class="button is-light is-small" title="Remove label" onclick="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelRemove({~D:Record.ID~})"><span class="icon"><i class="fas fa-trash"></i></span></button>`
	},
	{
		Hash: 'Headlight-ObsLabelAdd',
		Template: /*html*/`
			<tr class="otl-add">
				<td><input class="input is-small otl-input" type="text" placeholder="New label (e.g. Phase 1) — use | to add several" value="{~D:Record.NewTag~}" oninput="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelSetNewTag(this.value)" onkeydown="if (event.key === 'Enter') { event.preventDefault(); _Pict.providers['{~D:Record.ProviderName~}'].obsLabelAdd(); }" /></td>
				<td><select class="input is-small otl-input" onchange="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelPickType(this.value)">{~TS:Headlight-ObsLabelTypeOption:Record.TagTypeOptions~}</select></td>
				<td class="otl-act"><button class="button is-link is-small" onclick="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelAdd()"><span class="icon"><i class="fas fa-plus"></i></span><span>Add</span></button></td>
			</tr>`
	},
	{
		Hash: 'Headlight-ObsLabelTypeOption',
		Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Text~}</option>`
	},
	{
		Hash: 'Headlight-ObsLabelPager',
		Template: /*html*/`
			<div class="otl-pager">
				<span class="otl-range">{~D:Record.RangeLabel~}</span>
				<button class="button is-small" {~D:Record.PrevDisabled~} onclick="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelPage(-1)"><span class="icon"><i class="fas fa-chevron-left"></i></span><span>Prev</span></button>
				<button class="button is-small" {~D:Record.NextDisabled~} onclick="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelPage(1)"><span>Next</span><span class="icon"><i class="fas fa-chevron-right"></i></span></button>
			</div>`
	},
	{
		Hash: 'Headlight-ObsLabelBulk',
		Template: /*html*/`
			<details class="otl-bulk">
				<summary>Bulk add labels of a type</summary>
				<div class="otl-bulk-body">
					<div class="otl-bulk-row">
						<label class="otl-bulk-label">Type for all pasted labels</label>
						<select id="otl-bulk-type" class="input is-small otl-bulk-type" title="Every pasted label is added under this type" onchange="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelPickBulkType(this.value)">{~TS:Headlight-ObsLabelTypeOption:Record.TagTypeOptions~}</select>
					</div>
					<textarea id="otl-bulk-text" class="textarea is-small otl-bulk-text" rows="5" placeholder="Paste labels here — one per line, or separated by commas or | . Each is added under the type above." oninput="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelSetBulkTag(this.value)">{~D:Record.BulkTag~}</textarea>
					<div class="otl-bulk-actions">
						<button class="button is-link is-small" onclick="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelBulkAdd()"><span class="icon"><i class="fas fa-layer-group"></i></span><span>Add all</span></button>
					</div>
				</div>
			</details>`
	},
	{
		Hash: 'Headlight-ObsLabelNoMatch',
		Template: /*html*/`<p class="otl-empty">No labels match your search or filter. <a onclick="_Pict.providers['{~D:Record.ProviderName~}'].obsLabelClearSearch()">Clear the search</a> or pick a different type.</p>`
	},
	{
		Hash: 'Headlight-ObsLabelNoTypes',
		Template: /*html*/`<div class="otl-notypes"><span class="icon"><i class="fas fa-circle-info"></i></span><span>This project has no label types yet. Add one under <strong>Label types</strong> below — types populate the dropdown you pick from when adding a label.</span></div>`
	},
	{
		Hash: 'Headlight-ObsLabelEmpty',
		Template: /*html*/`<p class="otl-empty">No labels have been added to this project yet.</p>`
	},
	{
		Hash: 'Headlight-ObsLabelReadOnly',
		Template: /*html*/`<p class="otl-ro-note">You don’t have permission to change this project’s labels.</p>`
	},
	{
		Hash: 'Headlight-ObsLabelLoading',
		Template: /*html*/`<div class="otl-loading"><span class="otl-spin"></span>Loading labels…</div>`
	}
];
